import { sortLanguageTestApi, useGetLanguageTestList } from "@/api/services/global/master";
import { TGetLanguageTestListOutputDto } from "@/api/services/global/master/dtos/languageTest/get-master-language-test-list.dto";
import { IconOrderTable } from "@/assets/icons/IconOrderTable";
import { ConfirmLeavePage } from "@/components/commons/ConfirmLeavePage";
import { Button } from "@/components/elements/Button";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DATE_TIME_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { HiddenLanguageTestModal } from "@/features/master-data/components/LanguageTest/HiddenLanguageTestModal";
import { CreateLanguageTestFormModal } from "@/features/master-data/components/LanguageTest/CreateLanguageTestFormModal";
import { UpdateLanguageTestFormModal } from "@/features/master-data/components/LanguageTest/UpdateLanguageTestFormModal";
import { MASTER_DATA_TYPE_TITLE } from "@/features/master-data/constants/masterDataType";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { background, blue, button, divider, green, text, black } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { Box, Stack, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { Switch } from "@/components/elements/Switch";
import { FooterActionBar } from "@/components/commons/FooterActionBar";

export const MasterDataLanguageTest = () => {
  const updateLanguageTestFormModal = useVisible();
  const createLanguageTestFormModal = useVisible();
  const [hiddenLanguageTest, setHiddenLanguageTest] = useState<TGetLanguageTestListOutputDto>();
  const [selectedLanguageTest, setSelectedLanguageTest] = useState<TGetLanguageTestListOutputDto>();
  const [enableSaveOrderBtn, setEnableOrderBtn] = useState<boolean>(false);

  const { data, isLoading } = useGetLanguageTestList();

  const [rows, setRows] = useState<TGetLanguageTestListOutputDto[]>(data);

  useEffect(() => {
    if (data && data.length > 0) setRows(data);
  }, [data]);

  const handleSaveOrder = async () => {
    try {
      const rs = await sortLanguageTestApi({ ids: rows.map((item) => toNumber(item.id)) });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      setEnableOrderBtn(false);
    } catch (error) {
      showError(error);
    }
  };

  // Handle row dragging
  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !result.source || result.source.index === result.destination.index) return;
    const reorderedRows = Array.from(rows);
    const [removed] = reorderedRows.splice(result.source.index, 1);
    reorderedRows.splice(result.destination.index, 0, removed);
    setRows(reorderedRows);
    setEnableOrderBtn(true);
  };

  if (isLoading || rows.length === 0) return <LoadingPage />;

  return (
    <Stack sx={{ mt: 1 }}>
      <TopPage title={"「" + MASTER_DATA_TYPE_TITLE.LANGUAGE_TEST + "」の編集"} backUrl={AppRoutes.masterData} enablePageBack={false} />
      <StyledContainer>
        <Stack gap={2}>
          <Stack direction="row" justifyContent="flex-end" gap={3}>
            <Button size="sm" onClick={() => createLanguageTestFormModal.open()}>
              項目追加
            </Button>
          </Stack>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="tableRows" direction="vertical">
              {(provided) => (
                <StyledTable {...provided.droppableProps} ref={provided.innerRef}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell>試験名（日本語）</TableCell>
                        <TableCell>レベル</TableCell>
                        <TableCell>説明</TableCell>
                        <TableCell>最終更新日</TableCell>
                        <TableCell sx={{ position: "sticky", textAlign: "center !important", right: "100px", bgcolor: background.white }}>
                          有効／無効
                        </TableCell>
                        <TableCell sx={{ position: "sticky", textAlign: "center !important", right: "0", bgcolor: background.white }}>編集</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, index) => (
                        <Draggable key={row.id} draggableId={row.id.toString()} index={index}>
                          {(provided) => (
                            <TableRow ref={provided.innerRef} {...provided.draggableProps}>
                              <TableCell
                                sx={{
                                  width: "40px",
                                  textAlign: "center",
                                }}
                                {...provided.dragHandleProps}
                              >
                                <IconOrderTable color={blue[80]} />
                              </TableCell>
                              <TableCell sx={{ minWidth: "230px", paddingLeft: "8px !important" }}>
                                <Typography component="p" color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                  {row.testName || EMPTY_STR.TEXT}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ minWidth: "150px" }}>
                                {row.levels.length > 0 ? (
                                  row.levels.map((item, index, defaultArray) => (
                                    <Box
                                      sx={{
                                        lineHeight: "40px",
                                        height: "40px",
                                        borderBottom: index !== defaultArray.length - 1 ? "1px solid #E6E7E8" : "none",
                                      }}
                                      key={item.id}
                                    >
                                      <Typography color={item.isHidden ? text.tertiary : text.primary} variant="body14">
                                        {item.level || EMPTY_STR.TEXT}
                                      </Typography>
                                    </Box>
                                  ))
                                ) : (
                                  <Box>
                                    <Typography component="p" color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                      {EMPTY_STR.TEXT}
                                    </Typography>
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell sx={{ minWidth: "230px", paddingLeft: "8px !important" }}>
                                <Typography component="p" color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                  {row.description || EMPTY_STR.TEXT}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ minWidth: "150px" }}>
                                {row.levels.length > 0 ? (
                                  row.levels.map((item, index, defaultArray) => (
                                    <Box
                                      sx={{
                                        lineHeight: "40px",
                                        height: "40px",
                                        borderBottom: index !== defaultArray.length - 1 ? "1px solid #E6E7E8" : "none",
                                      }}
                                      key={item.id}
                                    >
                                      <Typography color={item.isHidden ? text.tertiary : text.primary} sx={{ px: 0.5 }} variant="body14">
                                        {item.updatedAt ? dayjs(item.updatedAt).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE}
                                      </Typography>
                                    </Box>
                                  ))
                                ) : (
                                  <Box
                                    sx={{
                                      lineHeight: "40px",
                                      height: "40px",
                                    }}
                                  >
                                    <Typography color={row.isHidden ? text.tertiary : text.primary} sx={{ px: 0.5 }} variant="body14">
                                      {row.updatedAt ? dayjs(row.updatedAt).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE}
                                    </Typography>
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell
                                sx={{
                                  textAlign: "center !important",
                                  minWidth: "120px",
                                  position: "sticky",
                                  right: "100px",
                                  bgcolor: background.white,
                                }}
                              >
                                <Switch checked={!row.isHidden} onChange={() => setHiddenLanguageTest(row)} color="primary" />
                              </TableCell>
                              <TableCell
                                sx={{
                                  minWidth: "100px",
                                  textAlign: "center !important",
                                  padding: "8px !important",
                                  position: "sticky",
                                  right: "0px",
                                  bgcolor: background.white,
                                }}
                              >
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedLanguageTest(row);
                                    updateLanguageTestFormModal.open();
                                  }}
                                >
                                  編集
                                </Button>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  </Table>
                </StyledTable>
              )}
            </Droppable>
          </DragDropContext>
        </Stack>

        <CreateLanguageTestFormModal onClose={createLanguageTestFormModal.close} isOpen={createLanguageTestFormModal.visible} />

        {updateLanguageTestFormModal.visible && (
          <UpdateLanguageTestFormModal
            onClose={updateLanguageTestFormModal.close}
            isOpen={updateLanguageTestFormModal.visible}
            selectedLanguageTest={selectedLanguageTest}
          />
        )}

        <HiddenLanguageTestModal hiddenLanguageTest={hiddenLanguageTest} setHiddenLanguageTest={setHiddenLanguageTest} />

        <ConfirmLeavePage shouldBlock={enableSaveOrderBtn} />
      </StyledContainer>
      <FooterActionBar title="オーダーを保存する" onSubmit={handleSaveOrder} backUrl={AppRoutes.masterData} isDisabled={!enableSaveOrderBtn} />
    </Stack>
  );
};

const StyledContainer = styled(Box)({
  padding: 24,
  marginTop: 24,
  border: `1px solid ${divider.middle}`,
  borderRadius: rounded.sm,
  backgroundColor: background.white,
  marginBottom: 120,
});

const StyledTable = styled(Box)`
  border-radius: ${rounded.sm};
  overflow: auto;
  max-height: 575px;
  border-top: 1px solid ${divider.middle};
  border-bottom: 1px solid ${divider.middle};
  border-right: 1px solid ${divider.middle};
  background-color: ${background.white};
  table {
    border-collapse: separate;
  }
  th,
  td {
    border-left: 1px solid ${divider.middle};
  }
  * {
    box-shadow: none;
  }
  .MuiToolbar-dense {
    display: none;
  }
  .MuiTableCell-head {
    padding: 8px !important;
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
    position: sticky;
    background-color: white;
    z-index: 500;
    top: 0;
    background: ${black[100]};
  }

  .Mui-TableHeadCell-Content-Labels {
    > span {
      margin-left: 4px;
      img {
        opacity: 0.2;
      }
      .MuiBadge-badge {
        display: none;
      }
    }
  }
  .MuiTableCell-body {
    font-size: 14px;
    font-weight: 400;
    line-height: 28px;
    > div {
      padding-left: 8px;
    }
  }

  // Default
  .MuiTableRow-root {
    background-color: ${background.white};
    box-shadow: none;
    cursor: initial;
    .MuiTableCell-root {
      padding: 0;
      border-color: ${divider.middle};
      opacity: 1;
    }
    &:not(.MuiTableRow-head):last-child {
      .MuiTableCell-root {
        border-bottom: 0;
      }
    }
  }
  // Hover
  .MuiTableRow-root:not(.MuiTableRow-head):hover {
    &,
    td {
      background-color: ${background.white};
      &::after {
        background-color: ${background.primary};
      }
    }
  }
  // Click/Pressed
  .Mui-selected {
    background-color: ${background.white} !important;
    td::after {
      background: ${background.secondary}!important;
    }
  }

  .MuiPaper-root {
    box-shadow: none;
  }
  .MuiPaper-root > .MuiBox-root {
    display: none;
  }

  .MuiCheckbox-root {
    color: ${button.primary}!important;
    height: 24px;
    width: 24px;
    padding: 0;
  }

  // Disabled Row
  .MuiTableCell-root.disabled {
    opacity: 1;
    background: ${background.disable}!important;
    pointer-events: none;
  }

  // added row
  .MuiTableCell-root.added {
    background: ${green[99]}!important;
    &::after {
      background: unset !important;
    }
  }

  // Failed row
  .MuiTableCell-root.failed {
    background: ${background.error}!important;
    &::after {
      background: unset !important;
    }
  }

  // Clickable row
  .MuiTableCell-root.clickable {
    cursor: pointer;
  }
`;
